import { EFinancingProgram, ETasksStatus } from '@if/core/types'
import {
  AccessTimeFilled as AccessTimeFilledIcon,
  CheckCircle as CheckCircleIcon,
  RemoveCircle as RemoveCircleIcon,
} from '@mui/icons-material'
import { CircularProgress } from '@mui/material'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import IconButtonWithTooltip from '../../components/IconButtonWithTooltip'
import { useTaskTypes } from '../../data/selectors/app-config-selectors'
import { CreditApplicationTask } from '../../data/types'

interface Props {
  task: CreditApplicationTask
  onClickStatusButton: (status: ETasksStatus, task: CreditApplicationTask) => void
  userCanEditTask: boolean
  editDisabled: boolean
  isUpdatingTask?: boolean
}

const StatusTasks = ({ task, onClickStatusButton, userCanEditTask, editDisabled, isUpdatingTask }: Props): JSX.Element | null => {
  const { t } = useTranslation()
  const { financingProgramId } = useParams<{ financingProgramId: EFinancingProgram }>()
  const taskTypes = useTaskTypes(financingProgramId)

  if (isUpdatingTask) {
    return <CircularProgress size={20} />
  }

  const taskType = taskTypes[task.typeId]
  const hasNoComment = (task.comments?.length ?? 0) === 0
  const isCommentRequiredAndMissing = taskType?.commentIsRequired === true && hasNoComment

  const completedDisabled =
    taskType?.statusIsManagedByTheSystem === true || !(userCanEditTask && !editDisabled) || isCommentRequiredAndMissing
  const completedTooltip = isCommentRequiredAndMissing ? t('tasks.commentRequired') : t('tasks.completed')

  const cancelDisabled = !(userCanEditTask && !editDisabled) || taskType?.canBeCancelled === false

  return (
    <>
      <IconButtonWithTooltip
        tooltip={t('tasks.toDo')}
        icon={<AccessTimeFilledIcon color={task.status === ETasksStatus.ToDo ? 'primary' : 'disabled'} />}
        onClick={() => onClickStatusButton(ETasksStatus.ToDo, task)}
        disabled={!(userCanEditTask && !editDisabled)}
      />

      <IconButtonWithTooltip
        tooltip={completedTooltip}
        icon={<CheckCircleIcon color={task.status === ETasksStatus.Completed ? 'success' : 'disabled'} />}
        onClick={() => onClickStatusButton(ETasksStatus.Completed, task)}
        disabled={completedDisabled}
      />

      <IconButtonWithTooltip
        tooltip={t('tasks.cancelled')}
        icon={<RemoveCircleIcon color={task.status === ETasksStatus.Cancelled ? 'error' : 'disabled'} />}
        onClick={() => onClickStatusButton(ETasksStatus.Cancelled, task)}
        disabled={cancelDisabled}
      />
    </>
  )
}

export default StatusTasks
